import React from 'react'
import styled, { css } from 'styled-components'
import ParseContent from 'components/shared/ParseContent'
import ButtonDefault from 'components/elements/ButtonDefault'
import ButtonSlide from 'components/elements/ButtonSlide'
import Plaatjie from '@ubo/plaatjie'
import Img from 'gatsby-image'

const ImgCSS = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledImg = styled(Img)`
  ${ImgCSS};
`

const StyledPlaatjie = styled(Plaatjie)`
  ${ImgCSS};
`

const StyledCta = styled.section`
  position: relative;
  z-index: 1;
  
  @media screen and (min-width: 992px) {
    padding-top: 100px;
    padding-bottom: 50px;
  } 

  @media screen and (max-width: 991px) {
    padding-top: 60px;
    padding-bottom: 10px;
  }
`

const StyledContainer = styled.div`
  height: 100%;

  @media screen and (max-width: 991px) {
    height: unset;
    position: relative;
  }
`

const Wrapper = styled.div`
  background-color: ${(props) => props.theme.color.face.light};
  border-radius: 20px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
  padding: 40px 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 48px;

  @media screen and (max-width: 991px) {
    padding: 28px;
    border-radius: 16px;
    flex-direction: column;
    gap: 32px;
  }

  @media screen and (max-width: 576px) {
    padding: 20px;
    border-radius: 12px;
    gap: 24px;
  }
`
const TextColumn = styled.div`
  flex: 1;

  @media screen and (max-width: 991px) {
    order: 1;
    text-align: center;
  }

  & h2 {
    font-weight: ${(props) => props.theme.font.weight.xl};

    @media screen and (min-width: 992px) {
      font-size: ${(props) => props.theme.font.size.xl};
    }

    @media screen and (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size.l};
    }
  }
`

const ButtonsBlock = styled.div`
  display: flex;
  flex-direction: row;

  @media screen and (min-width: 992px) {
    gap: 10px;
  }

  @media screen and (max-width: 991px) {
    flex-direction: column;
    gap: 10px;
  }
`

const StyledButtonSlide = styled(ButtonSlide)`
  @media screen and (max-width: 991px) {
    a, button {
      justify-content: center;
    }
  }
`

const MediaColumn = styled.div`
  flex-shrink: 0;

  @media screen and (max-width: 991px) {
    order: -1;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 220px;
  height: 220px;

  @media screen and (max-width: 576px) {
    width: 180px;
    height: 180px;
  }
`

const ImageBlock = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
`

const NameAndRoleBlock = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  background: ${(props) => props.theme.color.gradient.main};
  border-radius: 8px;
  padding: 8px 12px;
  color: ${(props) => props.theme.color.text.light};

  @media screen and (max-width: 576px) {
    padding: 6px 10px;
    border-radius: 6px;
  }
`

const withBoldFirstLine = (text) => {
  if (!text) return text
  const match = text.match(/^([\s\S]*?)(<br\s*\/?>|\n)([\s\S]*)$/)
  if (match) {
    return `<strong>${match[1]}</strong>${match[2]}${match[3]}`
  }
  return text
}

const Cta = ({ className = '', content, id = 'cta' }) => {
  if (!content) return null
  return (
    <StyledCta id={id} className={`${className}`}>
      <StyledContainer className="container">
        <Wrapper>
          <TextColumn>
            <h2>{content?.title}</h2>
            <ParseContent className="mt-3" content={content?.description} />
            <ButtonsBlock className="mt-4">
              {content?.link && (
                <ButtonDefault icon="arrow" to={content?.link?.url}>
                  {content?.link?.title}
                </ButtonDefault>
              )}
              {content?.link_two?.url && (
                <StyledButtonSlide icon="arrow" to={content?.link_two?.url} isAnchor defaultSize>
                  {content?.link_two?.title}
                </StyledButtonSlide>
              )}
            </ButtonsBlock>
          </TextColumn>
          <MediaColumn>
            <ImageWrapper>
              <ImageBlock>
                {!!content.image?.localFile?.childImageSharp?.fluid ? (
                  <StyledImg
                    fluid={content.image.localFile.childImageSharp.fluid}
                    loading="eager"
                    alt=""
                  />
                ) : (
                  <StyledPlaatjie image={content.image} loading="eager" alt="" />
                )}
              </ImageBlock>
              <NameAndRoleBlock>
                <ParseContent content={withBoldFirstLine(content?.name_and_role)} />
              </NameAndRoleBlock>
            </ImageWrapper>
          </MediaColumn>
        </Wrapper>
      </StyledContainer>
    </StyledCta>
  )
}

export default Cta
